// optionConstraints — config-driven narrowing of a select's options by the live
// value of ANOTHER field, shared by AddFormV1 and EditFormV1.
//
// Config key (NEW — must exist on the Go FormField struct or the admin save API
// drops it): `field.optionsFromField: "<otherFieldKey>"`.
//
// Use case it was built for: a candidate's rate UNIT may not differ from the
// unit the JOB was raised in. The job's unit is already brought onto the form by
// the existing `prefillFromModule` mechanism, so with this constraint the whole
// restriction is config — no module, field or value name is written in code.
//
// The referenced field is resolved with the SAME row-vs-top-level scoping the
// render side uses for `showIf` (row first, top level as fallback) — see
// FieldControl, which watches both paths and prefers the row value when set.

import { prefillWhenMatches } from './prefillWhenRules';

const asKey = (v) => String(v ?? '').trim().toLowerCase();

// Every comparable form of a constraint value: a plain scalar, an antd
// labelInValue object ({value,label}) or a reference object ({id,name}), and
// arrays of any of those (a multi-select constraint narrows to a SET).
function constraintKeys(constraintValue) {
  const list = Array.isArray(constraintValue) ? constraintValue : [constraintValue];
  const keys = [];
  list.forEach((item) => {
    if (item === undefined || item === null || item === '') return;
    if (typeof item === 'object') {
      [item.value, item.id, item._id, item.label, item.name].forEach((v) => {
        if (v !== undefined && v !== null && v !== '') keys.push(asKey(v));
      });
      return;
    }
    keys.push(asKey(item));
  });
  return keys;
}

/**
 * narrowOptionsByConstraint — the options a select may offer given the current
 * value of the field named by `optionsFromField`.
 *
 * Matching is case-insensitive on the option's value OR its label, so a stored
 * "hr" narrows to the option labelled "Hourly" with value "hr" either way.
 *
 * FAILS OPEN in both directions:
 *   • no constraint configured / constraint field still empty → all options
 *   • the constraint matches NO option → all options
 * A narrowing that resolves to an empty dropdown would leave the user unable to
 * fill a (possibly mandatory) field because of a data mismatch they cannot see
 * or fix, which is strictly worse than showing the unrestricted list.
 */
export function narrowOptionsByConstraint(options = [], constraintValue) {
  const keys = constraintKeys(constraintValue);
  if (!keys.length || !Array.isArray(options) || options.length === 0) return options;
  const wanted = new Set(keys);
  const narrowed = options.filter((option) => {
    if (option === null || option === undefined) return false;
    if (typeof option !== 'object') return wanted.has(asKey(option));
    return wanted.has(asKey(option.value)) || wanted.has(asKey(option.label));
  });
  return narrowed.length > 0 ? narrowed : options;
}

// ---------------------------------------------------------------------------
// prefillWhenExclusive — "a value a Conditional Default would SET is exclusive
// to that rule's condition".
//
// Config key (NEW — must exist on the Go FormField struct or the admin save API
// drops it): `field.prefillWhenExclusive: true`. Default OFF, so every field
// configured before this existed offers exactly the options it does today.
//
// It reuses the EXISTING `field.prefillWhen` rules rather than introducing a
// second list, so the value that must be hidden can never fall out of step with
// the value that gets auto-filled — there is only one copy of it in config.
//
//   prefillWhen: [{ field: 'contractType', value: 'W2', setValue: '<id>' }]
//     contractType = W2    → the rule matches → that option IS offered (and the
//                            existing watcher auto-selects it, as today)
//     contractType = C2C   → no rule matches → that option is REMOVED
//     contractType = empty → no rule matches → that option is REMOVED
//
// The use case: the value a W2 rule injects is the internal/own company; on any
// other contract type the employer must be an external vendor, so offering the
// internal one is wrong — not merely a bad default. Nothing here knows that:
// the rule, the field and the value are all admin data.
//
// DELIBERATE DIVERGENCE from narrowOptionsByConstraint's fail-open rule: this
// does NOT restore the full list when the exclusion empties it. Failing open on
// a NARROWING is right (a data mismatch the user cannot see must not block a
// mandatory field), but failing open on an EXCLUSION would re-offer precisely
// the value the admin declared unofferable, i.e. reintroduce the bug. An empty
// list is the honest answer — and these lookups carry a quick-create button, so
// the user still has a way forward. Config-level fail-open is kept: flag off,
// no rules, or a rule with no setValue all leave the options untouched.

// Every option key one rule claims: the value it sets and, when the admin UI
// stored one, its human label — so a rule whose setValue is a record id still
// matches an option carrying that record's label, and vice versa. Mirrors
// constraintKeys' value-OR-label matching above.
function ruleOptionKeys(rule) {
  const keys = [];
  [rule?.setValue, rule?.setValueLabel].forEach((v) => {
    if (v === undefined || v === null || v === '') return;
    keys.push(asKey(v));
  });
  return keys;
}

/**
 * exclusivePrefillBlockedKeys — the option keys that must NOT be offered right
 * now, given the rule set and the live values of the fields it references.
 *
 * A key claimed by a rule that DOES match is always allowed, even if another
 * (non-matching) rule claims the same key — one live reason to offer a value is
 * enough.
 *
 * Rules that CLEAR (`rule.clearValue`) set no value at all, so they claim
 * nothing and never hide an option.
 *
 * @param {Array}  rules    field.prefillWhen
 * @param {object} watched  { [referencedFieldKey]: liveValue }
 * @returns {string[]}
 */
export function exclusivePrefillBlockedKeys(rules, watched = {}) {
  const list = Array.isArray(rules) ? rules : [];
  const blocked = new Set();
  const allowed = new Set();
  list.forEach((rule) => {
    if (!rule || rule.clearValue) return;
    const keys = ruleOptionKeys(rule);
    if (!keys.length) return;
    const target = prefillWhenMatches(rule, watched) ? allowed : blocked;
    keys.forEach((key) => target.add(key));
  });
  allowed.forEach((key) => blocked.delete(key));
  return [...blocked];
}

/**
 * stripExclusivePrefillOptions — the options a control may offer once every
 * currently-inapplicable Conditional Default value has been removed.
 *
 * Case-insensitive on the option's value OR its label, exactly like
 * narrowOptionsByConstraint, so it works whether the option list is a lookup
 * (value = record id) or a static list (value = label).
 *
 * Composes with narrowOptionsByConstraint — pass its output in.
 *
 * @param {Array}  options  the already-narrowed option list
 * @param {Array}  rules    field.prefillWhen (only when the flag is on)
 * @param {object} watched  { [referencedFieldKey]: liveValue }
 */
export function stripExclusivePrefillOptions(options = [], rules, watched) {
  const blocked = exclusivePrefillBlockedKeys(rules, watched);
  if (!blocked.length || !Array.isArray(options) || options.length === 0) return options;
  const deny = new Set(blocked);
  return options.filter((option) => {
    if (option === null || option === undefined) return true;
    if (typeof option !== 'object') return !deny.has(asKey(option));
    return !(deny.has(asKey(option.value)) || deny.has(asKey(option.label)));
  });
}

export default {
  narrowOptionsByConstraint,
  exclusivePrefillBlockedKeys,
  stripExclusivePrefillOptions,
};
